// ja landing pages: see config/localeHubs.ts for the rules and
// config/hubs/ja.json for the slug of each page type (keep them in step).
//
// Volumes: OpenSEO, Japan, monthly (Oct 2026). Phrasing from Google
// autocomplete (hl=ja, gl=jp). Skipped on purpose: a clone page ("声 クローン"
// 40/mo; autocomplete only adds "無料"/"アプリ"/"自分の声"), and characters /
// celebrities pages (no Japanese volume researched yet).
import type { HubCategory, HubConfig } from "../hubPages";

const CATEGORIES_JA: HubCategory[] = [
  { label: "音楽", filter: "music" },
  { label: "アニメ", filter: "anime" },
  { label: "カートゥーン", filter: "cartoon" },
  { label: "ゲーム", filter: "gaming" },
  { label: "K-POP", filter: "korean" },
  { label: "有名人", filter: "public figure" },
];

// "無料" follows almost every search here ("ボイスチェンジャー 無料",
// "aiカバー 無料", "ai 歌わせる 無料"): answer honestly, never promise it.
const FREE_JA = {
  q: "無料で使えますか？",
  a: "すべてのボイスの閲覧と、サンプルカバーの試聴は無料です。カバーの作成、声の変換、声のクローン、テキスト読み上げにはプランが必要です。最新の料金は料金プランのページをご覧ください。",
};

export const HUBS: Record<string, HubConfig> = {
  // "ボイスチェンジャー" 18,100 (KD 3), "AIボイスチェンジャー" 1,600 (KD 0).
  // Autocomplete: "アプリ", "無料", "リアルタイム", "女声", "おすすめ", "pc",
  // "サイト", "ディスコード", "ボイスチェンジャーとは", "ai 歌", "ai 声優";
  // "声を変える", "声を変えるサイト". Results mix AI converters (ElevenLabs,
  // finevoice, myedit) with real-time apps (Voicemod): the real-time question
  // is answered plainly in the FAQ.
  "ai-voice-changer": {
    slug: "ai-voice-changer",
    group: "voice-changer",
    path: "/ja/ai-voice-changer",
    locale: "ja",
    h1: "AIボイスチェンジャーで声を変える",
    highlight: "AIボイスチェンジャー",
    title: "AIボイスチェンジャー｜録音した声を好きな声に変換 | Jammable",
    metaDescription:
      "AIボイスチェンジャーで、録音した声をキャラクター・歌手・有名人の声に変換。6万以上のリアルなAIボイスから選べます。ブラウザで使えるボイスチェンジャーサイト。",
    heroSub:
      "声を録音するか音声をアップロードして、なりたい声を選ぶだけ。数秒で別人の声に変わります。",
    intro:
      "AIボイスチェンジャーは、エフェクトで声を加工するだけのボイスチェンジャーとは違い、声そのものを別の声に変換します。アニメのキャラクター、歌手、有名人、自分で学習させた声まで選べます。",
    appName: "Jammable AIボイスチェンジャー",
    steps: [
      {
        title: "録音またはアップロード",
        body: "マイクで声を録音するか、変換したい音声ファイルをアップロードします。",
      },
      {
        title: "ボイスを選ぶ",
        body: "アニメやゲームのキャラクターから歌手、有名人まで、6万以上のAIボイスから選べます。",
      },
      {
        title: "声を変換",
        body: "数秒で新しい声の音声が完成。そのままダウンロードできます。",
      },
    ],
    faqs: [
      {
        q: "AIボイスチェンジャーとは？",
        a: "録音した音声を、AIの音声変換で別の声のように聞こえるようにするツールです。ピッチやエフェクトで加工する従来のボイスチェンジャーと違い、選んだ声本人が話しているような自然な仕上がりになります。",
      },
      {
        q: "リアルタイムで使えますか？",
        a: "Jammableは録音・アップロードした音声を変換します。ライブ配信や通話中のリアルタイム変換には対応していません。そのぶん、リアルタイム型のボイスチェンジャーよりも自然な声に仕上がります。変換した音声はダウンロードして、動画などに使えます。",
      },
      {
        q: "女声や男声に変えられますか？",
        a: "はい。女性の声のボイスを選べば女声に、男性の声のボイスを選べば男声に変換できます。声の高さの調整もできます。",
      },
      {
        q: "PCでもスマホでも使えますか？",
        a: "はい。Jammableはブラウザで動くボイスチェンジャーサイトなので、PCでもスマホでもインストールなしで使えます。",
      },
      {
        q: "キャラクターの声に変えられますか？",
        a: "はい。アニメ、ゲーム、カートゥーンのキャラクターの声が数千種類あり、毎日新しいボイスが追加されています。",
      },
      FREE_JA,
    ],
    categories: CATEGORIES_JA,
    related: [
      { label: "AIカバーを作る", href: "/ja/ai-cover" },
      { label: "自分の声を学習させる", href: "/train" },
      { label: "AIボイス一覧", href: "/models" },
    ],
  },

  // "AIカバー" 1,900 (KD 0), "ai cover" 1,300, "AI 歌わせる" 1,000 (KD 4),
  // "AI歌声" 70. Autocomplete: "aiカバー曲 作り方", "aiカバー やり方",
  // "作り方 スマホ", "著作権", "違法", "ai 歌わせる 自分の声 / 人の声 /
  // 有名人 / tiktok", "歌声 変換", "ai cover kpop".
  "ai-cover": {
    slug: "ai-cover",
    group: "cover",
    path: "/ja/ai-cover",
    locale: "ja",
    h1: "AIカバーで好きな声に歌わせよう",
    highlight: "AIカバー",
    title: "AIカバーの作り方｜好きな声でAIに歌わせる | Jammable",
    metaDescription:
      "AIカバー曲を数秒で作成。6万以上のAIボイスで、歌手・キャラクター・自分の声にAIで歌わせられます。歌声を変換して、スタジオ品質で仕上げます。",
    heroSub:
      "ボイスを選んで、使う権利のある音声を追加するだけ。数秒でAIカバーが完成します。",
    intro:
      "これまで曲の歌声を別の声に変えるには、スタジオと何時間ものミックス作業が必要でした。Jammableならボイスを選んで音声をアップロードするだけ。AIがボーカルを分離して歌声を変換し、もう一度ミックスします。",
    appName: "Jammable AIカバー",
    steps: [
      {
        title: "ボイスを選ぶ",
        body: "歌手、ラッパー、アニメやカートゥーンのキャラクター、自分で学習させた声など、6万以上のAIボイスから選べます。",
      },
      {
        title: "音声を追加",
        body: "音声ファイルをアップロードするか、自分の歌を録音するか、AIでオリジナル曲を生成します。",
      },
      {
        title: "AIカバーを作成",
        body: "Jammableが歌声を変換してミックスします。ダウンロードして、TikTokやYouTubeでシェアしましょう。",
      },
    ],
    faqs: [
      {
        q: "AIカバーとは？",
        a: "AIを使って、曲を別の声で歌わせたバージョンのことです。曲や自分の歌声を、アーティストやキャラクター、自分で学習させた声に変換します。メロディーと歌詞はそのままです。",
      },
      {
        q: "AIカバー曲の作り方は？",
        a: "Jammableでボイスを選び、音声をアップロードして作成を押すだけです。ボーカルと伴奏を分離し、選んだ声に変換して、1分以内にミックスまで仕上げます。スマホのブラウザからでも作れます。",
      },
      {
        q: "自分の声でAIに歌わせることはできますか？",
        a: "はい。6分以上のクリアな音声から自分の声のモデルを学習させれば、どのカバーにも使えます。",
      },
      {
        q: "AIカバーの著作権は大丈夫？",
        a: "アップロードできるのは、権利を持っている音声だけです。作ったカバーを公開するときは、元の曲の権利と各プラットフォームのルールを確認してください。AIカバーであることを明記するのがおすすめです。",
      },
      FREE_JA,
    ],
    categories: CATEGORIES_JA,
    related: [
      { label: "AIボイスチェンジャー", href: "/ja/ai-voice-changer" },
      { label: "自分の声を学習させる", href: "/train" },
      { label: "AIボイス一覧", href: "/models" },
    ],
  },
};
